import { useState } from 'react';
import { Info, X } from 'lucide-react';

interface CoachProfile {
  id: string;
  name: string;
  expertise: string;
  bio: string;
  whenToUse: string;
  specialties: string[];
}

interface CoachProfilePopoverProps {
  coach: CoachProfile;
  children: React.ReactNode;
}

const CoachProfilePopover = ({ coach, children }: CoachProfilePopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div
        className="relative inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {children}
        
        {/* Info Icons */}
        <button
          className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white hover:shadow-md transition-all duration-200 z-10"
          aria-label={`View ${coach.name}'s profile`}
        >
          <Info className="h-3.5 w-3.5 text-royal-blue" strokeWidth={2} />
        </button>
      </div>

      {/* Popover */}
      {isOpen && (
        <>
          {/* Mobile Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Popover Content */}
          <div
            className="fixed z-50 w-80 max-w-[90vw]"
            style={{
              left: `${Math.min(Math.max(position.x - 160, 16), window.innerWidth - 336)}px`,
              top: `${Math.max(position.y - 0, 16)}px`,
              transform: 'translateY(-100%)'
            }}
          >
            {/* Arrow */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="w-3 h-3 bg-white border-r border-b border-alto-grey transform rotate-45"></div>
            </div>
            
            {/* Content Card */}
            <div className="bg-white rounded-2xl border border-alto-grey shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-alto-grey">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-lg font-bold text-gray-900 tracking-tight">
                      {coach.name}
                    </h3>
                    <p className="text-sm font-display font-medium text-royal-blue uppercase tracking-wider">
                      {coach.expertise}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/60 text-gray-500 hover:text-gray-700 transition-colors md:hidden"
                    aria-label="Close profile"
                  >
                    <X className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {/* Bio Section */}
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-display text-sm font-semibold text-gray-900 mb-2">
                      About {coach.name}
                    </h4>
                    <p className="font-body text-sm text-gray-700 leading-relaxed">
                      {coach.bio}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-display text-sm font-semibold text-gray-900 mb-2">
                      When to use {coach.name}
                    </h4>
                    <p className="font-body text-sm text-gray-700 leading-relaxed">
                      {coach.whenToUse}
                    </p>
                  </div>

                  {/* Specialties */}
                  <div>
                    <h4 className="font-display text-sm font-semibold text-gray-900 mb-2">
                      Specialties
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {coach.specialties.map((specialty) => (
                        <span
                          key={specialty}
                          className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-display font-medium text-royal-blue"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CoachProfilePopover;
